import React, { useState, useRef, useEffect } from 'react';
import {
    Switch,
    Button,
    Col,
    Drawer,
    Form,
    Input,
    Row,
    Select,
    Space,
    message,
    DatePicker,
    Typography,
    InputNumber
} from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { insert, update, getById } from '../../../../apis/IncapacidadEmpleadoApi';
import { getList as getTiposIncapacidad } from '../../../../apis/TipoIncapacidadApi';
import { getEmpleados } from '../../../../apis/OdooApi';
import dayjs from 'dayjs';
import _ from 'lodash';

const { Option } = Select;
const { Text } = Typography;

const App = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [tiposIncapacidad, setTiposIncapacidad] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [fechaFinal, setFechaFinal] = useState(null);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;

            // Validar formato del número de incapacidad
            const numeroIncapacidad = values.numero_incapacidad;
            const regex = /^[A-Z]{2}\d{6}$/;
            if (!regex.test(numeroIncapacidad)) {
                message.error('El número de incapacidad debe tener 2 letras mayúsculas seguidas de 6 dígitos');
                return;
            }

        } catch (errorInfo) {
            message.error('Por favor, corrige los errores en el formulario antes de guardar.');
            return;
        }

        let success = false;
        const fields = form.getFieldsValue();

        const payload = {
            empleado_id: fields.odoo_empleado_id, // Mapear odoo_empleado_id a empleado_id
            tipo_incapacidad_id: fields.tipo_incapacidad_id,
            fecha_inicial: dayjs(fields.fecha_inicial).format('YYYY-MM-DD'), // Solo fecha sin hora
            dias_incapacidad: fields.dias_incapacidad,
            numero_incapacidad: fields.numero_incapacidad,
            control: fields.control,
            porcentaje_perm: fields.porcentaje_perm,
            secuela: fields.secuela, // Mapear secuela a secuela_consecuencia
            activo: fields.activo,
            fecha_final: fechaFinal ? dayjs(fechaFinal).format('YYYY-MM-DD') : null};

        if (ID > 0) {
            // Update

            payload.id = ID;
            const response = await update(payload);
            success = response.ok;
            if(response.ok){
                message.success('Se actualizó correctamente la incapacidad!');
            }
        } else {
            const response = await insert(payload);
            success = response.ok;
            if(response.ok){
                setID(response.payload);
                message.success('Se guardó correctamente la incapacidad!');
            }
        }

        if (success) {
            setModified(true);
            onClose(true);
        }
    }

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
        setFechaFinal(null);
    }

        const calculateFechaFinal = (fechaInicial, dias) => {
                if (!fechaInicial || !dias) return null;

                // Clonar la fecha para no mutar el valor original del formulario
                const baseDate = dayjs.isDayjs(fechaInicial)
                        ? fechaInicial.clone()
                        : dayjs(fechaInicial);

                // Calcular fecha final sumando días y restando 1 día
                const fecha = baseDate.clone().add(dias, 'days').subtract(1, 'days');
                setFechaFinal(fecha);
                return fecha;
        }

    const handleDiasChange = (value) => {
        const fechaInicial = form.getFieldValue('fecha_inicial');
        if (fechaInicial) {
            calculateFechaFinal(fechaInicial, value);
        }
    }

    const handleFechaInicialChange = (date) => {
        // date ya debería ser un objeto dayjs desde el DatePicker
        const dias = form.getFieldValue('dias_incapacidad');
        if (dias && date) {
          calculateFechaFinal(date, dias);
        }
      }

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                await getCatalogs();
                await getEmpleadosOdoo();

                if (ID > 0) {
                    await refreshData();
                } else {
                    setInitialValues({
                        activo: true,
                        porcentaje_perm: 0,
                        fecha_inicial: dayjs(),
                        control: 0,
                        secuela: 0
                    });
                }
                setModified(false);
            })();
        }
        return () => effectRan.current = true;
    }, []);

    const refreshData = async () => {
        const response = await getById(ID);
        if(response.ok){
          let item = response.payload;
          const formValues = {
            ...item,
            odoo_empleado_id: item.empleado_id,
            secuela: item.secuela,
            fecha_inicial: dayjs(item.fecha_inicial), // Convertir a dayjs aquí
          };
          setInitialValues(formValues);
          if (item.fecha_inicial && item.dias_incapacidad) {
            calculateFechaFinal(item.fecha_inicial, item.dias_incapacidad);
          }
        }
      }

    const getCatalogs = async () => {
        const responseTiposIncapacidad = await getTiposIncapacidad();
        if (responseTiposIncapacidad.ok) {
            let items = responseTiposIncapacidad.payload;
            setTiposIncapacidad(items);
        }
    }

    const getEmpleadosOdoo = async () => {
        const response = await getEmpleados();
        if (response.ok) {
            let items = response.payload;
            setEmpleados(items);
        }
    }

    return (
        <Drawer
            title={title}
            width={720}
            placement={"right"}
            onClose={onClose}
            open={open}
            closable={true}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button onClick={handleOk} type="primary">
                        Guardar
                    </Button>
                </Space>
            }
        >
            {initialValues &&
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark
                    autoComplete="off"
                    initialValues={initialValues}
                    onFinish={handleOk}
                >
                    <Row gutter={16}>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="odoo_empleado_id"
                                label="Empleado"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Selecciona un empleado',
                                    },
                                ]}
                            >
                                <Select placeholder="Selecciona un empleado">
                                    {empleados.map((item) =>
                                        <Option value={item.value}>{item.text}</Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="tipo_incapacidad_id"
                                label="Tipo de Incapacidad"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Selecciona un tipo de incapacidad',
                                    },
                                ]}
                            >
                                <Select placeholder="Selecciona tipo de incapacidad">
                                    {tiposIncapacidad.map((item) =>
                                        <Option value={item.value}>{item.text}</Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="fecha_inicial"
                                label="Fecha Inicial"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Ingresa la fecha inicial',
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    onChange={handleFechaInicialChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="dias_incapacidad"
                                label="Días Incapacidad"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Ingresa los días de incapacidad',
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%' }}
                                    onChange={handleDiasChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item label="Fecha Final">
                                <Text strong>
                                    {fechaFinal ? fechaFinal.format('DD/MM/YYYY') : 'Calculada automáticamente'}
                                </Text>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="numero_incapacidad"
                                label="Número Incapacidad"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Ingresa el número de incapacidad (2 letras + 6 números)',
                                    },
                                    {
                                        pattern: /^[A-Z]{2}\d{6}$/,
                                        message: 'Formato inválido (2 letras mayúsculas + 6 números)',
                                    }
                                ]}
                            >
                                <Input placeholder="Ejemplo: AB123456" />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="control"
                                label="Control"
                            >
                                <Select placeholder="Selecciona tipo de control">
                                    <Option value={0}>Ninguno</Option>
                                    <Option value={1}>Única</Option>
                                    <Option value={2}>Inicial</Option>
                                    <Option value={3}>Subsecuente</Option>
                                    <Option value={4}>Alta Médica ó ST-2</Option>
                                    <Option value={5}>Prenatal</Option>
                                    <Option value={6}>Enlance</Option>
                                    <Option value={7}>PostNatal</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="porcentaje_perm"
                                label="%perm"
                                rules={[
                                    {
                                        type: 'number',
                                        min: 0,
                                        max: 100,
                                        message: 'Debe ser un valor entre 0 y 100',
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    step={0.0001}
                                    precision={4}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="secuela"
                                label="Secuela/consecuencia"
                            >
                                <Select placeholder="Selecciona secuela/consecuencia">
                                    <Option value={0}>Ninguna</Option>
                                    <Option value={1}>Inc. Temporal</Option>
                                    <Option value={2}>Valuación Inicial Provisional</Option>
                                    <Option value={3}>Valuación Inicial Definitiva</Option>
                                    <Option value={4}>Defunción</Option>
                                    <Option value={5}>Recaída</Option>
                                    <Option value={6}>Valuación posterior a la fecha de alta</Option>
                                    <Option value={7}>Revaluación provisional</Option>
                                    <Option value={9}>Revaluación definitiva</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            }
        </Drawer>
    );
};

export default App;